import React from 'react';
import PublicNav from '../components/PublicNav';
import MarketingFooter from '../components/MarketingFooter';
import { Motif } from '../components/ui/Motif';
import { Reveal, RevealGroup } from '../components/ui/Reveal';

const BG = '#F5FAFC';
const INK = '#002A38';
const MUTED = '#78716C';
const TERRA = '#0076A3';
const LINE = '#ECE7DE';

const LinkedInIcon = (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
  </svg>
);

const FacebookIcon = (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
);

const CHANNELS = [
  {
    key: 'linkedin',
    name: 'LinkedIn',
    handle: 'company/chire',
    desc: 'Follow us for product updates, hiring insights, and company news.',
    url: 'https://www.linkedin.com/company/chire/',
    color: '#0A66C2',
    tile: '#E7F0FA',
    icon: LinkedInIcon,
  },
  {
    key: 'facebook',
    name: 'Facebook',
    handle: 'getchire',
    desc: 'Join our community and keep up with everything happening at CHIRE.',
    url: 'https://www.facebook.com/getchire',
    color: '#1877F2',
    tile: '#E7F0FE',
    icon: FacebookIcon,
  },
];

export default function ConnectWithUsPage() {
  return (
    <div style={{ minHeight: '100vh', background: BG, color: INK, overflowX: 'hidden' }} data-testid="connect-with-us-page">
      <PublicNav />

      {/* ─── Hero ─── */}
      <section style={{ position: 'relative', padding: 'clamp(3rem, 6vw, 5.5rem) clamp(1.25rem, 5vw, 5rem) clamp(1.5rem, 3vw, 2.5rem)', maxWidth: 960, margin: '0 auto', textAlign: 'center' }}>
        <div className="grain-overlay" style={{ opacity: 0.16 }} />
        <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: '10%', left: '6%', width: 40, color: TERRA, opacity: 0.85 }} />
        <Motif shape="ring" className="motif-float" stroke style={{ position: 'absolute', top: '18%', right: '6%', width: 64, color: '#A9DCF2', opacity: 0.6, animationDelay: '0.8s' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Reveal now>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.9rem', background: '#FFEADD', border: '1px solid #F0500033', borderRadius: 999, marginBottom: '1.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#D84400', letterSpacing: '0.04em' }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: '#F05000' }} /> We'd love to hear from you
            </div>
          </Reveal>
          <Reveal delay={0.1} now>
            <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2.5rem, 5.5vw, 4rem)', fontWeight: 800, lineHeight: 1.03, letterSpacing: '-0.03em', marginBottom: '1.1rem' }}>
              Connect with <span style={{ color: TERRA }}>Us</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2} now>
            <p style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.2rem)', color: MUTED, lineHeight: 1.6, maxWidth: 560, margin: '0 auto' }}>
              Questions, partnerships, or just want to say hello? Reach out and follow CHIRE on your favourite platform — we're always happy to connect.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Social channels ─── */}
      <section style={{ padding: '1rem clamp(1.25rem, 4vw, 3rem) clamp(2.5rem, 5vw, 4rem)', maxWidth: 900, margin: '0 auto' }}>
        <RevealGroup stagger={0.08} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {CHANNELS.map((c) => (
            <Reveal key={c.key}>
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`connect-${c.key}-link`}
                style={{ textDecoration: 'none', display: 'block', height: '100%' }}
              >
                <div
                  style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 22, padding: '2rem', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 26px 52px -30px rgba(28,25,23,0.4)'; e.currentTarget.style.borderColor = c.color; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = LINE; }}
                >
                  <div style={{ width: 60, height: 60, borderRadius: 16, background: c.tile, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                    {c.icon}
                  </div>
                  <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.375rem', fontWeight: 800, marginBottom: '0.35rem', color: INK }}>{c.name}</h3>
                  <p style={{ fontSize: '0.9375rem', color: MUTED, lineHeight: 1.6, marginBottom: '1.25rem', flexGrow: 1 }}>{c.desc}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 700, color: c.color }}>
                    <span>Follow on {c.name}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </RevealGroup>

        {/* ─── Email band ─── */}
        <Reveal delay={0.15}>
          <div style={{ marginTop: '1.75rem', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 22, padding: 'clamp(1.5rem, 3vw, 2rem)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1.25rem' }}>
            <div>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.3rem', color: INK }}>Prefer email?</h3>
              <p style={{ fontSize: '0.9375rem', color: MUTED, lineHeight: 1.6, maxWidth: 440 }}>
                Drop us a line and our team will get back to you shortly.
              </p>
            </div>
            <a
              href="mailto:connect@chire.co"
              data-testid="connect-email-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #00A0E0 0%, #F05000 100%)', color: '#fff', borderRadius: 999, fontWeight: 700, fontSize: '0.9375rem', padding: '0.75rem 1.6rem', textDecoration: 'none', boxShadow: '0 12px 28px -14px rgba(240,80,0,0.6)', transition: 'transform 0.2s, opacity 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.95'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.opacity = '1'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
              connect@chire.co
            </a>
          </div>
        </Reveal>
      </section>

      <MarketingFooter />
    </div>
  );
}
